import type { Horizon, RadarFilters } from '../types/market';

const defaultFilters: RadarFilters = {
  horizon: '15m',
  direction: 'all',
  market: 'all',
  search: '',
  minQuoteVolume: 1_000_000,
  minDataQuality: 45,
};

const FILTERS_STORAGE_KEY = 'pilum.radarFilters.v1';

const VALID_HORIZONS = new Set<Horizon>(['5m', '15m', '30m', '1h', '4h']);
const VALID_DIRECTIONS = new Set<RadarFilters['direction']>(['all', 'long', 'short']);
const VALID_MARKETS = new Set<RadarFilters['market']>(['all', 'spot', 'futures']);
const VALID_QUOTE_VOLUMES = new Set<number>([500_000, 1_000_000, 5_000_000, 25_000_000, 100_000_000]);
const VALID_DATA_QUALITIES = new Set<number>([25, 45, 60, 75]);

/**
 * Restore the filters the user chose on a previous visit so selections survive
 * full page reloads, not just in-app navigation. Every field is validated
 * against its allowed values so a corrupted or stale entry can never render a
 * broken control (e.g. an empty <select>).
 */
export function loadPersistedFilters(): RadarFilters {
  try {
    const raw = window.localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return { ...defaultFilters };
    const parsed = JSON.parse(raw) as Partial<RadarFilters>;
    return {
      horizon: VALID_HORIZONS.has(parsed.horizon as Horizon) ? (parsed.horizon as Horizon) : defaultFilters.horizon,
      direction: VALID_DIRECTIONS.has(parsed.direction as RadarFilters['direction'])
        ? (parsed.direction as RadarFilters['direction'])
        : defaultFilters.direction,
      market: VALID_MARKETS.has(parsed.market as RadarFilters['market']) ? (parsed.market as RadarFilters['market']) : defaultFilters.market,
      search: typeof parsed.search === 'string' ? parsed.search : defaultFilters.search,
      minQuoteVolume: VALID_QUOTE_VOLUMES.has(parsed.minQuoteVolume as number)
        ? (parsed.minQuoteVolume as number)
        : defaultFilters.minQuoteVolume,
      minDataQuality: VALID_DATA_QUALITIES.has(parsed.minDataQuality as number)
        ? (parsed.minDataQuality as number)
        : defaultFilters.minDataQuality,
    };
  } catch {
    return { ...defaultFilters };
  }
}

/** Persist the current filters so the next page load restores them. */
export function persistFilters(filters: RadarFilters): void {
  try {
    window.localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // storage unavailable; in-memory filters still work for the session
  }
}
